<template>
  <ActionMenu :menuOptions="menuOptions" iconType="kebab" />
</template>

<script lang="ts">
import { sendSegmentEvent } from '@/lib/segment';
import sitterDetailsForParent from '@/apps/messages/helpers/sitterDetails';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';
import ActionMenu from '@/components/action_menu/ActionMenu.vue';
import { MenuItemOptions } from '@/components/action_menu/__types__/ActionMenuTypes';
import ApplicationIcon from '@/graphics/icons/ApplicationIcon.vue';
import StarOutlineIcon from '@/graphics/icons/StarOutlineIcon.vue';
import AddAvailabilityIcon from '@/graphics/icons/AddAvailabilityIcon.vue';
import TrustAndSafetyIcon from '@/graphics/icons/TrustAndSafetyIcon.vue';
import WarningOutlineIcon from '@/graphics/icons/WarningOutlineIcon.vue';
import { getSupportTicketHref, buildReportMenuItem } from '@/apps/messages/helpers/channelHeaderActionsReport';

export default {
  name: 'ChannelHeaderActionsSeeker',
  components: {
    ActionMenu,
  },
  props: {
    correspondingUserAccountId: {
      type: String,
      default: '',
    },
    correspondingUserId: {
      type: String,
      default: '',
    },
    currentChannelUrl: {
      type: String,
      default: '',
    },
  },
  data(): {
    canReview: boolean;
    sitterHasVisibleProfile: boolean;
    sitterProviderType: string | null;
  } {
    return {
      canReview: false,
      sitterHasVisibleProfile: true,
      sitterProviderType: null,
    };
  },
  computed: {
    menuOptions(): MenuItemOptions[] {
      const options: MenuItemOptions[] = [];

      if (this.sitterHasVisibleProfile) {
        const viewProfileOption = this.buildViewProfileOption();
        if (viewProfileOption) {
          options.push(viewProfileOption);
        }
        options.push(this.buildScheduleInterviewOption());
      }

      if (this.canReview) {
        const writeReviewOption = this.buildWriteReviewOption();
        if (writeReviewOption) {
          options.push(writeReviewOption);
        }
      }

      options.push(
        this.buildBackgroundCheckOption(),
        buildReportMenuItem(getSupportTicketHref(this.currentChannelUrl), WarningOutlineIcon, 24, 24)
      );

      return options;
    },
  },
  watch: {
    correspondingUserId() {
      this.updateSitterDetails();
    },
  },
  mounted() {
    this.updateSitterDetails();
  },
  methods: {
    async updateSitterDetails() {
      if (!this.correspondingUserId) return;
      const details = await sitterDetailsForParent(this.correspondingUserId);
      this.canReview = details?.canReview ?? false;
      this.sitterHasVisibleProfile = details?.hasVisibleProfile ?? true;
      this.sitterProviderType = details?.providerType ?? null;
    },
    buildViewProfileOption(): MenuItemOptions | null {
      if (!this.correspondingUserId) return null;
      const profileUrl = getProviderProfileUrl(this.correspondingUserId, null, this.sitterProviderType);
      if (!profileUrl) return null;
      return {
        icon: ApplicationIcon,
        height: 24,
        width: 20,
        iconClasses: 'mr-18 ml-2',
        labelText: 'View profile',
        href: profileUrl,
        target: '_blank',
        click: () => {
          sendSegmentEvent('Viewed Profile in Message', {
            viewed_profile_id: this.correspondingUserId,
          });
        },
      };
    },
    buildScheduleInterviewOption(): MenuItemOptions {
      return {
        icon: AddAvailabilityIcon,
        height: 24,
        width: 24,
        iconClasses: 'availability-icon',
        labelText: 'Schedule interview',
        to: { path: `/interview-request/${this.correspondingUserId}/new` },
        click: () => {},
      };
    },
    buildWriteReviewOption(): MenuItemOptions | null {
      if (!this.correspondingUserId) return null;
      return {
        icon: StarOutlineIcon,
        height: 24,
        width: 24,
        labelText: 'Write a review',
        to: { path: `/ratings/sitters/${this.correspondingUserId}/new` },
        click: () => {},
      };
    },
    buildBackgroundCheckOption(): MenuItemOptions {
      return {
        icon: TrustAndSafetyIcon,
        height: 24,
        width: 24,
        labelText: 'Run background check',
        href: `/background-check/sponsored-checks/${this.correspondingUserAccountId}/new`,
        target: '_self',
        click: () => {
          sendSegmentEvent('Run Background Check in Message', {
            requested_user_id: this.correspondingUserAccountId,
          });
        },
      };
    },
  },
};
</script>
